<template>
  <div>
    <mf-info-card v-if="isIndustryCampaign && !isMercapromoCampaign" :v-html="cashbackTypeInfo" class="mb-4" />
    <mf-card-container v-if="showUngroupedTable" :title="title" :hide-card-shadow="hideCardShadow">
      <cashback-and-points-table
        :ref="offerSalesType"
        class="mt-4"
        :offer-sales-type="offerSalesType"
        type="ungrouped"
        :products="ungroupedProducts"
        :validations="$v"
        :headers="headers"
        :helpers="helpers"
        :repeated-skus="repeatedSkus"
        :search="search"
        :is-disabled-in-rules="isDisabledInRules"
        :is-industry-campaign="isIndustryCampaign"
        :tooltip-message="tooltipMessage"
        :disabled="disabled"
        @remove-item="removeItem"
      />
    </mf-card-container>

    <div v-if="showGroupedTable">
      <h2 :class="showUngroupedTable ? 'pt-8' : ''" class="ma-0 pb-0 offers-title">Grupos na oferta</h2>
      <p class="offers-subtitle pt-3">
        {{ groupDescription }}
      </p>

      <group-card
        v-for="(group, index) in groupedProducts"
        :key="group._id"
        :offer-sales-type="offerSalesType"
        :group="group"
        :headers="headers"
        :helpers="helpers"
        :repeated-skus="repeatedSkus"
        :validations="$v"
        :group-index="index"
        :search="search"
        :is-disabled-in-rules="isDisabledInRules"
        :is-industry-campaign="isIndustryCampaign"
        :tooltip-message="tooltipMessage"
        :disabled="disabled"
        @remove-item="removeItem"
        @remove-group="removeGroup(group._id)"
        @change-group-exhibition="changeGroupExhibition($event, group._id)"
      />
    </div>
  </div>
</template>

<script>
import GroupCard from '../GroupCard.vue'
import CashbackAndPointsTable from './CashbackAndPointsTable.vue'

import { required, requiredIf } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import {
  cashbackPercentageValidation,
  eansValidation,
  price1Validation,
  minMaxPerClientValidation,
  minMaxPerClientValidationNextbuy,
} from '@/modules/campanhas/gestao-ofertas-v2/helpers/validators.js'

import { offerSalesTablesContainer } from '../../../mixins/offer-sales-tables-container'

export default {
  name: 'CashbackAndPoints',
  components: {
    GroupCard,
    CashbackAndPointsTable,
  },
  mixins: [offerSalesTablesContainer],
  props: {
    offerSalesType: {
      type: String,
      required: true,
      validator: (val) => {
        return ['cashback_percentage_offer_sales', 'cashback_absolute_offer_sales', 'points_offer_sales', 'next_buy_cashback_offer_sales'].includes(val)
      },
    },
    isMercapromoCampaign: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    title() {
      const titles = {
        cashback_percentage_offer_sales: 'cashback percentual',
        cashback_absolute_offer_sales: 'cashback absoluto',
        points_offer_sales: 'pontos',
        next_buy_cashback_offer_sales: 'cashback',
      }
      return this.ungroupedProducts.length === 1
        ? `1 produto desagrupado com ${titles[this.offerSalesType]}`
        : `${this.ungroupedProducts.length} produtos desagrupados com ${titles[this.offerSalesType]}`
    },
    headers() {
      let cashbackOrPoints = {
        width: '9%',
        sortable: false,
      }

      if (this.offerSalesType === 'cashback_percentage_offer_sales') {
        cashbackOrPoints.value = 'cashback_percentage'
        cashbackOrPoints.text = 'Cashback (%)'
      } else if (this.offerSalesType === 'cashback_absolute_offer_sales') {
        cashbackOrPoints.value = 'cashback_absolute'
        cashbackOrPoints.text = 'Cashback (R$)'
      } else if (this.offerSalesType === 'points_offer_sales') {
        cashbackOrPoints.value = 'additional_points_rate'
        cashbackOrPoints.text = 'Pontos'
      } else if (this.isNextbuyCashback) {
        cashbackOrPoints.value = 'cashback_absolute_discount'
        cashbackOrPoints.text = 'Cashback (R$)'
      }

      let headers = [
        {
          text: '',
          value: 'expandAction',
          width: '2%',
          sortable: false,
        },
        {
          text: '',
          value: 'image',
          width: '5%',
          sortable: false,
        },
        {
          text: 'SKU',
          value: 'sku',
          width: '10%',
          sortable: false,
        },
        {
          text: 'EAN',
          value: 'ean',
          width: '10%',
          sortable: false,
        },
        {
          text: 'Nome',
          value: 'name',
          width: '30%',
          sortable: false,
        },
        {
          text: 'Preço 1',
          value: 'price_1',
          width: '9%',
          sortable: false,
        },
        { ...cashbackOrPoints },
        {
          text: 'Mín. CPF',
          value: 'minimum_per_client',
          width: '7.5%',
          sortable: false,
          tooltip: this.isNextbuyCashback ? 'Quantidade mínima do produto no cupom para ativar a oferta' : null,
        },
        {
          text: 'Máx. CPF',
          value: this.isNextbuyCashback ? 'max_coupons' : 'maximum_per_client',
          width: '7.5%',
          sortable: false,
          tooltip: this.isNextbuyCashback ? 'Máximo de vezes que um cliente poderá receber o cashback' : null,
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false,
          width: '8%',
        },
        {
          text: '',
          width: '4%',
          value: 'action',
          sortable: false,
        },
      ]

      if (this.isIndustryCampaign) {
        const priceIndex = headers.findIndex((h) => h.value === 'price_1')
        headers.splice(priceIndex, 1)
      }
      return headers
    },

    isNextbuyCashback() {
      return this.offerSalesType === 'next_buy_cashback_offer_sales'
    },

    cashbackTypeInfo() {
      if (this.isNextbuyCashback) {
        return `<span style="color: #004d99">
        O modelo de cashback ativo na sua conta é o <b>Cashback na próxima compra</b>.
        Quando seu consumidor receber um cashback, ele será aplicado automaticamente na próxima compra,
        <b>sem que ele precise ativar no aplicativo</b>.
      </span>`
      } else {
        return `<span style="color: #004d99">
        O modelo de cashback ativo na sua conta é o <b>Cashback na carteira</b>. 
        Quando seu consumidor receber um cashback, para que ele possa consumi-lo na próxima compra,
        <b>ele precisará ativá-lo antes pelo aplicativo</b>.
      </span>`
      }
    },
  },

  validations() {
    const priceLabel = this.offerSalesType === 'cashback_percentage_offer_sales' ? 'price_1' : 'price'
    const minMaxValidation = this.isNextbuyCashback ? { minMaxPerClientValidationNextbuy } : { minMaxPerClientValidation }
    const fields = {
      required: requiredIf(function () {
        return this.allInputsRequired
      }),
      $each: {
        sku: requiredIf(function (group) {
          return !group.products
        }),
        name: { required, notOnlySpace },
        eans: { eansValidation },
        minMaxPerClientValidation: minMaxValidation,
        [priceLabel]: !this.isIndustryCampaign ? { price1Validation: price1Validation(this.isFranchise) } : {},
        ...(this.offerSalesType === 'cashback_percentage_offer_sales' ? { cashback_percentage: { cashbackPercentageValidation } } : {}),
        ...(this.offerSalesType === 'cashback_absolute_offer_sales' ? { cashback_absolute: { cashbackPercentageValidation } } : {}),
        ...(this.offerSalesType === 'points_offer_sales' ? { additional_points_rate: { cashbackPercentageValidation } } : {}),
        ...(this.isNextbuyCashback ? { cashback_absolute_discount: { cashbackPercentageValidation } } : {}),
        products: {
          $each: {
            eans: {
              eansValidation,
            },
            name: { required, notOnlySpace },
          },
        },
      },
    }
    return {
      ...(this.ungroupedProducts.length > 0
        ? {
            ungroupedProducts: { ...fields },
          }
        : {}),

      ...(this.groupedProducts.length > 0
        ? {
            groupedProducts: { ...fields },
          }
        : {}),
    }
  },
}
</script>

<style lang="scss" scoped>
.offers-subtitle {
  font-size: 16px;
  line-height: 120%;
  letter-spacing: 0.4px;
  color: $cinza-0;
  max-width: 930px;
}

.offers-title {
  font-size: 26px;
  font-weight: 700;
  color: $cinza-d10;
}
</style>
